import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Package, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/services/api/authApi';
import { registrationApi } from '@/services/api/registrationApi';
import { Button, Input } from '@/components/ui';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const customerSchema = z.object({
  email: z.string().min(1, 'Введите email').email('Некорректный email'),
  password: z.string().min(8, 'Минимум 8 символов'),
  full_name: z.string().min(1, 'Введите полное имя'),
  phone: z.string().optional(),
  atomi_id: z.coerce.number({ error: 'Введите число' }).int().positive('Должно быть положительным числом'),
});

type CustomerForm = z.infer<typeof customerSchema>;

const RegisterCustomerPage: React.FC = () => {
  const { loginWithTokens, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CustomerForm>({
    resolver: zodResolver(customerSchema) as any,
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/my-orders', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: CustomerForm) => {
    try {
      const tokenResponse = await registrationApi.registerCustomer({
        email: data.email,
        password: data.password,
        full_name: data.full_name,
        phone: data.phone || undefined,
        atomi_id: data.atomi_id,
      });

      loginWithTokens(tokenResponse);

      const user = await authApi.getMe();
      useAuthStore.getState().setUser(user);

      toast.success('Регистрация завершена!');
      navigate('/my-orders', { replace: true });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error(extractErrorMessage(err, 'Email или Atomi ID уже зарегистрирован'));
      } else {
        toast.error(extractErrorMessage(err, 'Ошибка регистрации. Попробуйте позже.'));
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary-600 flex items-center justify-center mx-auto mb-4 shadow-card">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Регистрация клиента</h1>
          <p className="text-sm text-gray-500 mt-1">Создайте аккаунт для отслеживания заказов</p>
        </div>

        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6 sm:p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Полное имя"
                placeholder="Иванов Иван Иванович"
                error={errors.full_name?.message}
                required
                {...register('full_name')}
              />
              <Input
                label="Email"
                type="email"
                placeholder="user@example.com"
                error={errors.email?.message}
                required
                {...register('email')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Пароль"
                type="password"
                placeholder="••••••••"
                error={errors.password?.message}
                required
                {...register('password')}
              />
              <Input
                label="Телефон"
                type="tel"
                placeholder="+7 (999) 123-45-67"
                error={errors.phone?.message}
                {...register('phone')}
              />
            </div>

            <Input
              label="Atomi ID"
              type="number"
              placeholder="12345"
              error={errors.atomi_id?.message}
              required
              {...register('atomi_id')}
            />

            <p className="text-sm text-gray-500 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              Выбрать пункт выдачи можно в личном кабинете после регистрации
            </p>

            <Button
              type="submit"
              loading={isSubmitting}
              className="w-full"
            >
              Зарегистрироваться
            </Button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Вернуться ко входу
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterCustomerPage;
